import { useEffect, useRef, useState, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";

interface UseNavigationBlockReturn {
  showModal: boolean;
  navigateTo: string;
  confirmNavigation: () => void;
  cancelNavigation: () => void;
}

/**
 * Hook to block navigation when user tries to leave the page.
 * Intercepts link clicks, browser back/forward buttons, programmatic navigation, and browser refresh/close.
 * 
 * @param shouldBlock - Boolean to enable/disable navigation blocking
 * @returns Object with modal state and navigation handlers (showModal, navigateTo, confirmNavigation, cancelNavigation)
 * 
 * @example
 * const { showModal, confirmNavigation, cancelNavigation } = useNavigationBlock(showUploadSection);
 * 
 * // Use showModal to control ConfirmModal visibility
 * // Use confirmNavigation when user confirms navigation
 * // Use cancelNavigation when user cancels navigation
 */
export const useNavigationBlock = (shouldBlock: boolean): UseNavigationBlockReturn => {
  const location = useLocation();
  const navigate = useNavigate();
  const previousPathRef = useRef(location.pathname);
  const [openMessageModal, setOpenMessageModal] = useState(false);
  const [navigateTo, setNavigateTo] = useState("");
  const [pendingNavigation, setPendingNavigation] = useState<(() => void) | null>(null);

  // Block navigation when shouldBlock is true
  useEffect(() => {
    if (!shouldBlock) {
      previousPathRef.current = location.pathname;
      return;
    }

    // Warn on browser refresh/close
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = "";
    };

    // Intercept all link clicks
    const handleClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const link = target.closest('a[href]') as HTMLAnchorElement;
      
      if (link && link.href) {
        const url = new URL(link.href);
        const currentUrl = new URL(window.location.href);
        
        // Only block if navigating to a different path
        if (url.pathname !== currentUrl.pathname) {
          e.preventDefault();
          e.stopPropagation();
          setOpenMessageModal(true);
          setNavigateTo(url.pathname + url.search);
          setPendingNavigation(() => () => {
            navigate(url.pathname + url.search);
          });
        }
      }
    };

    // Intercept browser back/forward
    const handlePopState = (e: PopStateEvent) => {
      if (shouldBlock) {
        // Push state back to prevent navigation
        window.history.pushState(null, "", location.pathname);
        setOpenMessageModal(true);
        setNavigateTo(previousPathRef.current);
        setPendingNavigation(() => () => {
          window.history.back();
        });
      }
    };

    // Push dummy state to intercept back button
    window.history.pushState({ blocked: true }, "", location.pathname);

    window.addEventListener("beforeunload", handleBeforeUnload);
    window.addEventListener("popstate", handlePopState);
    document.addEventListener("click", handleClick, true); // Use capture phase

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
      window.removeEventListener("popstate", handlePopState);
      document.removeEventListener("click", handleClick, true);
    };
  }, [shouldBlock, location.pathname, navigate]);

  // Also detect programmatic navigation (navigate() calls)
  useEffect(() => {
    if (!shouldBlock || previousPathRef.current === location.pathname) {
      previousPathRef.current = location.pathname;
      return;
    }

    // Navigation occurred - block it
    window.history.replaceState(null, "", previousPathRef.current);
    setOpenMessageModal(true);
    setNavigateTo(location.pathname);
    setPendingNavigation(() => () => {
      navigate(location.pathname);
    });
  }, [location.pathname, shouldBlock, navigate]);

  const confirmNavigation = useCallback(() => {
    if (pendingNavigation) {
      pendingNavigation();
    } else if (navigateTo) {
      navigate(navigateTo);
    }
    setOpenMessageModal(false);
    setPendingNavigation(null);
  }, [pendingNavigation, navigateTo, navigate]);

  const cancelNavigation = useCallback(() => {
    setOpenMessageModal(false);
    setNavigateTo("");
    setPendingNavigation(null);
    previousPathRef.current = location.pathname;
  }, [location.pathname]);

  return {
    showModal: openMessageModal,
    navigateTo,
    confirmNavigation,
    cancelNavigation,
  };
};

